import { Link } from 'react-router-dom';
import { PowerOff, SlidersHorizontal } from 'lucide-react';
import { useAuthStore, StaffRole } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';
import { usePanelFeaturesStore } from '@/stores/panelFeatures.store';
import type { ModuleOff } from '@/stores/projectPages.store';
import { getGameById } from '@/config/games';
import styles from './ModuleDisabled.module.scss';

/**
 * Garde de route des modules (cf AdminLayout) : l'URL d'un module coupé pour le projet actif a été
 * ouverte directement. La page n'est pas montée ; on dit quel module est coupé, à quel niveau, et
 * un admin a le lien vers le réglage. C'est un confort de navigation, comme le masquage de la barre
 * latérale : les permissions de grade restent le contrôle d'accès.
 */
export function ModuleDisabled({ moduleKey, off }: { moduleKey: string; off: ModuleOff }) {
  const features = usePanelFeaturesStore((s) => s.features);
  const user = useAuthStore((s) => s.user);
  const activeGameId = useGameStore((s) => s.activeGameId);
  const gameName = getGameById(activeGameId).name;
  // Paramètres généraux : AdminRoute, donc admin GLOBAL seulement (un admin de projet n'y entre pas).
  const isAdmin = !!user?.isAdmin || user?.role === StaffRole.ADMIN;
  const labelOf = (key: string) => features.find((f) => f.key === key)?.label ?? key;
  const global = off.scope === 'global';
  // Section coupée par son module parent (Pays des statistiques sans Statistiques) : on nomme les deux.
  const viaParent = off.key !== moduleKey;

  return (
    <div className={styles.wrap} role="status">
      <span className={styles.icon} aria-hidden><PowerOff size={22} /></span>
      <h1 className={styles.title}>
        {global ? 'Module désactivé sur tout le panel' : 'Module désactivé pour ce projet'}
      </h1>
      <p className={styles.text}>
        {global
          ? <>« {labelOf(off.key)} » est coupé pour tous les projets.</>
          : <>« {labelOf(off.key)} » n'est pas activé pour {gameName}.</>}
        {viaParent && <> « {labelOf(moduleKey)} » en fait partie.</>}
      </p>
      <p className={styles.hint}>
        {isAdmin
          ? 'Tu peux le réactiver dans Paramètres généraux > Fonctionnalités.'
          : global
            ? "Demande à un administrateur du panel de le réactiver."
            : "Change de projet dans le sélecteur, ou demande à un administrateur du panel de l'activer."}
      </p>
      {isAdmin && (
        <Link to={`/parametres/fonctionnalites?module=${encodeURIComponent(off.key)}`} className={styles.action}>
          <SlidersHorizontal size={14} aria-hidden /> Ouvrir les réglages des fonctionnalités
        </Link>
      )}
    </div>
  );
}
